import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import Spinner from '../ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Button } from '../ui/Kit';

const { FiZap, FiChevronDown, FiChevronUp } = FiIcons;

const STATUS_STYLES = {
  pending: { bg: 'rgba(234, 179, 8, 0.15)', color: '#ca8a04', label: 'Pending' },
  in_progress: { bg: 'rgba(0, 170, 255, 0.12)', color: '#00AAFF', label: 'In Progress' },
  completed: { bg: 'rgba(34, 197, 94, 0.12)', color: '#16a34a', label: 'Completed' },
  failed: { bg: 'rgba(239, 68, 68, 0.12)', color: '#dc2626', label: 'Failed' },
};

const OUTCOME_STYLES = {
  appointment_booked: { bg: 'rgba(34, 197, 94, 0.12)', color: '#16a34a', label: 'Appointment Booked' },
  qualified: { bg: 'rgba(0, 170, 255, 0.12)', color: '#00AAFF', label: 'Qualified' },
  not_interested: { bg: 'rgba(107, 114, 128, 0.12)', color: '#6b7280', label: 'Not Interested' },
  no_answer: { bg: 'rgba(107, 114, 128, 0.12)', color: '#6b7280', label: 'No Answer' },
  voicemail: { bg: 'rgba(107, 114, 128, 0.12)', color: '#6b7280', label: 'Voicemail' },
};

function formatThoughtlyDuration(sec) {
  const total = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function formatTimestamp(iso) {
  if (!iso) return '—';
  try {
    const d = parseISO(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, 'MMM d, yyyy h:mm a');
  } catch {
    return '—';
  }
}

function Pill({ style, label }) {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {label}
    </span>
  );
}

function ThoughtlyCallCard({ call, agentName }) {
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const statusStyle = STATUS_STYLES[call.status] || {
    bg: 'rgba(107, 114, 128, 0.12)',
    color: '#6b7280',
    label: call.status || '—',
  };
  const outcomeStyle = call.outcome ? OUTCOME_STYLES[call.outcome] : null;
  const triggeredBy =
    call.triggered_by_user?.full_name || call.triggered_by_user?.email || '—';
  const ts = call.updated_at || call.created_at;
  const showAppointmentBanner =
    call.appointment_booked || call.outcome === 'appointment_booked';

  return (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
      {showAppointmentBanner && (
        <div className="px-4 py-2 bg-green-50 border-b border-green-100 text-sm font-medium text-green-800">
          📅 Appointment Booked
        </div>
      )}
      <div className="p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-gray-900">{agentName || 'AI Agent'}</p>
            <p className="text-xs text-gray-500 mt-0.5">Triggered by {triggeredBy}</p>
          </div>
          <span className="text-xs text-gray-400 whitespace-nowrap">{formatTimestamp(ts)}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Pill style={statusStyle} label={statusStyle.label} />
          {outcomeStyle && <Pill style={outcomeStyle} label={outcomeStyle.label} />}
          {call.duration_seconds != null && call.duration_seconds > 0 && (
            <span className="text-xs text-gray-500 font-mono self-center">
              {formatThoughtlyDuration(call.duration_seconds)}
            </span>
          )}
        </div>
        {call.transcript?.trim() && (
          <div className="border-t border-gray-100 pt-2">
            <button
              type="button"
              onClick={() => setTranscriptOpen((o) => !o)}
              className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80"
            >
              <SafeIcon icon={transcriptOpen ? FiChevronUp : FiChevronDown} className="w-3.5 h-3.5" />
              {transcriptOpen ? 'Hide transcript' : 'View transcript'}
            </button>
            {transcriptOpen && (
              <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-md p-3 border border-gray-100">
                {call.transcript}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AiCallsTab({
  calls,
  agents = [],
  loading,
  onSendToAiCaller,
  hasPhone,
}) {
  const agentNameByThoughtlyId = React.useMemo(() => {
    const map = {};
    for (const a of agents) {
      if (a.thoughtly_agent_id) map[a.thoughtly_agent_id] = a.name;
    }
    return map;
  }, [agents]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 gap-2 text-gray-500 text-sm">
        <Spinner className="text-primary" />
        Loading AI calls…
      </div>
    );
  }

  if (!calls.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
          <SafeIcon icon={FiZap} className="w-5 h-5 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-700">No AI calls yet</p>
        <p className="text-xs text-gray-400 mt-1 max-w-sm">
          Click Send to AI Caller to get started
        </p>
        {hasPhone && onSendToAiCaller && (
          <Button size="sm" className="mt-4" variant="primary" onClick={onSendToAiCaller}>
            <SafeIcon icon={FiZap} className="mr-2" />
            Send to AI Caller
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {calls.map((call) => (
        <ThoughtlyCallCard
          key={call.id}
          call={call}
          agentName={agentNameByThoughtlyId[call.thoughtly_agent_id]}
        />
      ))}
    </div>
  );
}
